import { Component, ElementRef, HostListener, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BzgModalRef, BzgModalService } from 'bz-genome-angular';
import { ApiKeyData } from '../core/models/api-key';
import { ApiKeyService } from '../core/services/api-key.service';
import { ShowToastService } from 'app/shared/services/showToast.service';

const API_KEY_EXPIRATION_RANGE_DAYS = {
  min: 1,
  max: 365
};

const API_KEY_BLOBS_LIFETIME_RANGE_HOURS = {
  min: 1,
  max: 365 * 24
};

@Component({
  selector: 'security-api-key-configuration',
  templateUrl: './security-api-key-configuration.component.html',
  styleUrls: ['./security-api-key-configuration.component.scss']
})
export class SecurityApiKeyConfigurationComponent implements OnInit {

  @ViewChild('generatedApiKeyModal') generatedApiKeyModal: TemplateRef<any>;
  @ViewChild('confirmGenerateApiKeyModal') confirmGenerateApiKeyModal: TemplateRef<any>;
  @ViewChild('blobsDurationControl') blobsDurationControlRef: ElementRef<HTMLElement>;
  @ViewChild('blobLifeTimeDaysOptionsContainer') blobLifeTimeDaysOptionsContainerRef: ElementRef<HTMLElement>;
  @ViewChild('blobLifeTimeHoursOptionsContainer') blobLifeTimeHoursOptionsContainerRef: ElementRef<HTMLElement>;

  public apiKeyConfiguration: ApiKeyData = new ApiKeyData();
  public expirationDateInput: Date | null = null;
  public minExpirationDate: Date;
  public maxExpirationDate: Date;
  public selectedBlobLifeTimeDays: number = 0;
  public selectedBlobLifeTimeHours: number = 1;
  public blobLifeTimeDaysInputValue: number = 0;
  public blobLifeTimeHoursInputValue: number = 1;
  public blobLifeTimeDaysOptions: number[] = [];
  public blobLifeTimeHoursOptions: number[] = [];
  public isBlobsLifeTimePickerOpen: boolean = false;
  public isBlobsLifeTimeDaysDropdownOpen: boolean = false;
  public isBlobsLifeTimeHoursDropdownOpen: boolean = false;
  public hasInteractedWithExpirationDate: boolean = false;
  public hasInteractedWithBlobsLifeTime: boolean = false;
  public showDisableApiKeyWarning: boolean = false;
  public currentEnvironment: string = '';
  public isDevelopEnvironment: boolean = false;
  public blobsLifeTimePopupStyles: { [key: string]: string } = {};
  private previousEnabledValue: boolean = false;
  private persistedExpirationDate: Date | null = null;

  public generatedApiKey: string = '';
  public isLoadingConfiguration: boolean = false;
  public isGeneratingApiKey: boolean = false;
  public isSavingConfiguration: boolean = false;
  public modalRef: BzgModalRef;
  public confirmModalRef: BzgModalRef;
  public expirationRangeDays = API_KEY_EXPIRATION_RANGE_DAYS;
  public blobsLifeTimeRangeHours = API_KEY_BLOBS_LIFETIME_RANGE_HOURS;

  constructor(
    private _apiKeyService: ApiKeyService,
    private _translateService: TranslateService,
    private _showToastService: ShowToastService,
    private _modalService: BzgModalService
  ) { }

  ngOnInit(): void {
    this.getCurrentEnvironment();
    this.initializeBlobLifeTimeOptions();
    this.setExpirationDateLimits();
    this.loadApiKeyConfiguration();
  }

  loadApiKeyConfiguration(): void {
    this.isLoadingConfiguration = true;
    this._apiKeyService.getApiKeyConfigurationStatus().subscribe((response: ApiKeyData) => {
      this.apiKeyConfiguration = response;
      this.persistedExpirationDate = this.parseDateValue(response.ExpirationDate);
      this.expirationDateInput = this.parseDateValue(response.ExpirationDate);
      this.previousEnabledValue = !!this.apiKeyConfiguration.Enabled;
      this.showDisableApiKeyWarning = false;

      if (!this.apiKeyConfiguration.IsConfigured && !this.isExpirationDateValid()) {
        this.expirationDateInput = new Date(this.minExpirationDate.getTime());
      }

      this.apiKeyConfiguration.BlobsLifeTime = this.apiKeyConfiguration.BlobsLifeTime || '';
      this.setBlobsLifeTimeSelectionFromValue(this.apiKeyConfiguration.BlobsLifeTime, this.apiKeyConfiguration.IsConfigured);

      this.hasInteractedWithExpirationDate = !this.isExpirationDateValid();
      this.hasInteractedWithBlobsLifeTime = !this.isBlobsLifeTimeValid();

      this.isLoadingConfiguration = false;
    }, () => {
      this.isLoadingConfiguration = false;
    });
  }

  generateApiKey(): void {
    if (!this.canGenerateApiKey()) {
      return;
    }

    this.hasInteractedWithExpirationDate = true;
    this.hasInteractedWithBlobsLifeTime = true;

    if (!this.isExpirationDateValid()) {
      this._showToastService.showToast(this.getExpirationDateRangeMessage(), 'warning');
      return;
    }

    if (!this.isBlobsLifeTimeValid()) {
      this._showToastService.showToast(this.getBlobsLifeTimeRangeMessage(), 'warning');
      return;
    }

    this.apiKeyConfiguration.ExpirationDate = this.expirationDateInput;
    this.isGeneratingApiKey = true;
    this._apiKeyService.generateApiKey(this.apiKeyConfiguration).subscribe((response: { ApiKey: string }) => {
      this.generatedApiKey = response.ApiKey;
      this.apiKeyConfiguration.IsConfigured = true;
      this.persistedExpirationDate = this.parseDateValue(this.expirationDateInput);
      this.openGeneratedApiKeyModal();
      this.isGeneratingApiKey = false;
    }, () => {
      this.isGeneratingApiKey = false;
    });
  }

  onGenerateApiKeyClick(): void {
    if (!this.canGenerateApiKey()) {
      return;
    }

    if (this.apiKeyConfiguration && this.apiKeyConfiguration.IsConfigured) {
      this.openConfirmGenerateApiKeyModal();
      return;
    }

    this.generateApiKey();
  }

  confirmGenerateApiKey(): void {
    this.closeConfirmGenerateApiKeyModal();
    this.generateApiKey();
  }

  saveConfiguration(): void {
    this.hasInteractedWithExpirationDate = true;
    this.hasInteractedWithBlobsLifeTime = true;

    if (this.shouldValidateConfiguredFields() && !this.isExpirationDateValid()) {
      this._showToastService.showToast(this.getExpirationDateRangeMessage(), 'warning');
      return;
    }

    if (this.shouldValidateConfiguredFields() && !this.isBlobsLifeTimeValid()) {
      this._showToastService.showToast(this.getBlobsLifeTimeRangeMessage(), 'warning');
      return;
    }

    this.apiKeyConfiguration.ExpirationDate = this.expirationDateInput;
    this.isSavingConfiguration = true;
    this._apiKeyService.saveApiKeyConfiguration(this.apiKeyConfiguration).subscribe(() => {
      this.persistedExpirationDate = this.parseDateValue(this.expirationDateInput);
      this._showToastService.showToast(this._translateService.instant('GLOBAL.THE_OPERATION_WAS_SAVED_SUCCESSFULLY'), 'success');
      this.isSavingConfiguration = false;
    }, () => {
      this.isSavingConfiguration = false;
    });
  }

  canGenerateApiKey(): boolean {
    return !this.isGeneratingApiKey
      && !this.isSavingConfiguration
      && !!this.apiKeyConfiguration
      && this.apiKeyConfiguration.Enabled
      && this.isExpirationDateValid()
      && this.isBlobsLifeTimeValid();
  }

  closeGeneratedApiKeyModal(): void {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = null;
    }
  }

  closeConfirmGenerateApiKeyModal(): void {
    if (this.confirmModalRef) {
      this.confirmModalRef.close();
      this.confirmModalRef = null;
    }
  }

  private openGeneratedApiKeyModal(): void {
    if (this.modalRef) {
      this.modalRef.close();
    }

    this.modalRef = this._modalService.open(this.generatedApiKeyModal, {
      closeOnBackdropClick: false,
      closeOnRouterNavigation: true,
      maxWidth: '640px'
    });
  }

  private openConfirmGenerateApiKeyModal(): void {
    if (this.confirmModalRef) {
      this.confirmModalRef.close();
    }

    this.confirmModalRef = this._modalService.open(this.confirmGenerateApiKeyModal, {
      closeOnBackdropClick: false,
      closeOnRouterNavigation: true,
      maxWidth: '640px'
    });
  }

  copyApiKey(): void {
    if (!this.generatedApiKey) {
      return;
    }

    const textArea = document.createElement('textarea');
    textArea.style.position = 'fixed';
    textArea.style.left = '0';
    textArea.style.top = '0';
    textArea.style.opacity = '0';
    textArea.value = this.generatedApiKey;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);

    this._showToastService.showToast(this._translateService.instant('GLOBAL.TEXT_COPIED'), 'success');
  }

  isExpirationDateValid(): boolean {
    const selectedDate = this.normalizeDate(this.expirationDateInput);
    const minDate = this.normalizeDate(this.minExpirationDate);
    const maxDate = this.normalizeDate(this.maxExpirationDate);

    if (!selectedDate || !minDate || !maxDate) {
      return false;
    }

    return selectedDate.getTime() >= minDate.getTime() && selectedDate.getTime() <= maxDate.getTime();
  }

  shouldShowExpirationDateWarning(): boolean {
    return this.shouldValidateConfiguredFields() && this.hasInteractedWithExpirationDate && !this.isExpirationDateValid();
  }

  isApiKeyExpired(): boolean {
    if (!this.apiKeyConfiguration || !this.apiKeyConfiguration.IsConfigured || !this.apiKeyConfiguration.Enabled) {
      return false;
    }

    const expirationDate = this.normalizeDate(this.persistedExpirationDate);
    if (!expirationDate) {
      return false;
    }

    const today = this.normalizeDate(new Date());
    return !!today && expirationDate.getTime() <= today.getTime();
  }

  isBlobsLifeTimeValid(): boolean {
    const totalHours = this.parseBlobsLifeTimeToHours(this.apiKeyConfiguration.BlobsLifeTime);

    if (totalHours === null) {
      return false;
    }

    return totalHours >= this.blobsLifeTimeRangeHours.min && totalHours <= this.blobsLifeTimeRangeHours.max;
  }

  shouldShowBlobsLifeTimeWarning(): boolean {
    return this.shouldValidateConfiguredFields() && this.hasInteractedWithBlobsLifeTime && !this.isBlobsLifeTimeValid();
  }

  shouldHighlightBlobsLifeTimeRequiredIcon(): boolean {
    const value = this.apiKeyConfiguration && this.apiKeyConfiguration.BlobsLifeTime
      ? this.apiKeyConfiguration.BlobsLifeTime.trim()
      : '';

    return this.shouldValidateConfiguredFields() && this.hasInteractedWithBlobsLifeTime && !value;
  }

  canSaveConfiguration(): boolean {
    if (this.isGeneratingApiKey || this.isSavingConfiguration || !this.apiKeyConfiguration) {
      return false;
    }

    if (!this.shouldValidateConfiguredFields()) {
      return true;
    }

    return this.isExpirationDateValid() && this.isBlobsLifeTimeValid();
  }

  onExpirationDateChange(): void {
    this.hasInteractedWithExpirationDate = true;
  }

  onEnabledChange(value: boolean): void {
    const wasEnabled = this.previousEnabledValue;
    this.previousEnabledValue = value;

    if (this.apiKeyConfiguration && this.apiKeyConfiguration.IsConfigured && wasEnabled && !value) {
      this.showDisableApiKeyWarning = true;
      return;
    }

    if (value) {
      this.showDisableApiKeyWarning = false;
    }
  }

  onBlobsLifeTimeChange(): void {
    this.hasInteractedWithBlobsLifeTime = true;
  }

  onBlobsLifeTimeSelectionChange(): void {
    if (!this.apiKeyConfiguration || !this.apiKeyConfiguration.Enabled) {
      return;
    }

    this.hasInteractedWithBlobsLifeTime = true;
    this.syncBlobsLifeTimeInputValues();
    this.updateBlobsLifeTimeFromSelection();
  }

  onBlobsLifeTimeDaysInputChange(rawValue: number | string): void {
    this.selectedBlobLifeTimeDays = this.parseAndClampLifetimeValue(rawValue, this.expirationRangeDays.max);
    this.onBlobsLifeTimeSelectionChange();
    this.scrollBlobLifeTimeOptionIntoView('days', this.selectedBlobLifeTimeDays);
  }

  onBlobsLifeTimeHoursInputChange(rawValue: number | string): void {
    this.selectedBlobLifeTimeHours = this.parseAndClampLifetimeValue(rawValue, 23);
    this.onBlobsLifeTimeSelectionChange();
    this.scrollBlobLifeTimeOptionIntoView('hours', this.selectedBlobLifeTimeHours);
  }

  openBlobsLifeTimeDaysDropdown(): void {
    if (!this.apiKeyConfiguration || !this.apiKeyConfiguration.Enabled) {
      return;
    }

    this.isBlobsLifeTimeDaysDropdownOpen = true;
    this.isBlobsLifeTimeHoursDropdownOpen = false;
    this.scrollBlobLifeTimeOptionIntoView('days', this.selectedBlobLifeTimeDays);
  }

  openBlobsLifeTimeHoursDropdown(): void {
    if (!this.apiKeyConfiguration || !this.apiKeyConfiguration.Enabled) {
      return;
    }

    this.isBlobsLifeTimeHoursDropdownOpen = true;
    this.isBlobsLifeTimeDaysDropdownOpen = false;
    this.scrollBlobLifeTimeOptionIntoView('hours', this.selectedBlobLifeTimeHours);
  }

  selectBlobLifeTimeDays(day: number): void {
    if (!this.apiKeyConfiguration || !this.apiKeyConfiguration.Enabled) {
      return;
    }

    this.selectedBlobLifeTimeDays = day;
    this.isBlobsLifeTimeDaysDropdownOpen = false;
    this.onBlobsLifeTimeSelectionChange();
  }

  selectBlobLifeTimeHours(hour: number): void {
    if (!this.apiKeyConfiguration || !this.apiKeyConfiguration.Enabled) {
      return;
    }

    this.selectedBlobLifeTimeHours = hour;
    this.isBlobsLifeTimeHoursDropdownOpen = false;
    this.onBlobsLifeTimeSelectionChange();
  }

  getVisibleBlobLifeTimeDaysOptions(): number[] {
    return this.blobLifeTimeDaysOptions;
  }

  getVisibleBlobLifeTimeHoursOptions(): number[] {
    return this.blobLifeTimeHoursOptions;
  }

  openBlobsLifeTimePicker(event: MouseEvent): void {
    if (!this.apiKeyConfiguration || !this.apiKeyConfiguration.Enabled) {
      return;
    }

    event.stopPropagation();
    this.isBlobsLifeTimePickerOpen = true;
    this.isBlobsLifeTimeDaysDropdownOpen = false;
    this.isBlobsLifeTimeHoursDropdownOpen = false;
    this.updateBlobsLifeTimePopupPosition();
  }

  toggleBlobsLifeTimePicker(event: MouseEvent): void {
    if (!this.apiKeyConfiguration || !this.apiKeyConfiguration.Enabled) {
      return;
    }

    event.stopPropagation();
    this.isBlobsLifeTimePickerOpen = !this.isBlobsLifeTimePickerOpen;
    this.isBlobsLifeTimeDaysDropdownOpen = false;
    this.isBlobsLifeTimeHoursDropdownOpen = false;

    if (this.isBlobsLifeTimePickerOpen) {
      this.updateBlobsLifeTimePopupPosition();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isBlobsLifeTimePickerOpen) {
      this.updateBlobsLifeTimePopupPosition();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (!target) {
      return;
    }

    if (target.closest('.bmc-api-key-configuration__blobs-duration-control')) {
      return;
    }

    this.isBlobsLifeTimePickerOpen = false;
    this.isBlobsLifeTimeDaysDropdownOpen = false;
    this.isBlobsLifeTimeHoursDropdownOpen = false;
  }

  private updateBlobsLifeTimePopupPosition(): void {
    const controlElement = this.blobsDurationControlRef && this.blobsDurationControlRef.nativeElement;

    if (!controlElement) {
      return;
    }

    const controlRect = controlElement.getBoundingClientRect();
    this.blobsLifeTimePopupStyles = {
      top: `${controlRect.bottom + 6}px`,
      left: `${controlRect.right}px`
    };
  }

  getBlobsLifeTimeSelectionLabel(): string {
    const daysLabel = `${this.selectedBlobLifeTimeDays} ${this.getDurationUnitLabel(this.selectedBlobLifeTimeDays, 'day')}`;
    const hoursLabel = `${this.selectedBlobLifeTimeHours} ${this.getHourUnitLabel(this.selectedBlobLifeTimeHours)}`;
    const joiner = this._translateService.instant('API_KEY_CONFIGURATION.DURATION_JOINER');

    return `${daysLabel} ${joiner} ${hoursLabel}`;
  }

  getExpirationDateRangeMessage(): string {
    const minDuration = this.buildDurationLabel(this.expirationRangeDays.min);
    const maxDuration = this.buildDurationLabel(this.expirationRangeDays.max);

    return this._translateService.instant('API_KEY_CONFIGURATION.EXPIRATION_DATE_ALLOWED_RANGE', {
      minDuration,
      maxDuration
    });
  }

  getBlobsLifeTimeRangeMessage(): string {
    const minDuration = this.buildHoursDurationLabel(this.blobsLifeTimeRangeHours.min);
    const maxDuration = this.buildHoursDurationLabel(this.blobsLifeTimeRangeHours.max);

    return this._translateService.instant('API_KEY_CONFIGURATION.BLOBS_LIFETIME_ALLOWED_RANGE', {
      minDuration,
      maxDuration
    });
  }

  getExpirationDateRangeByFieldMessage(): string {
    const infoMessage = this._translateService.instant('API_KEY_CONFIGURATION.EXPIRATION_DATE_INFO');
    return `${infoMessage} ${this.getExpirationDateRangeMessage()}`;
  }

  getBlobsLifeTimeRangeByFieldMessage(): string {
    const infoMessage = this._translateService.instant('API_KEY_CONFIGURATION.BLOBS_LIFETIME_INFO');
    return `${infoMessage} ${this.getBlobsLifeTimeRangeMessage()}`;
  }

  private setExpirationDateLimits(): void {
    const minDate = this.normalizeDate(this.addDaysToDate(new Date(), this.expirationRangeDays.min));
    const maxDate = this.normalizeDate(this.addDaysToDate(new Date(), this.expirationRangeDays.max));

    this.minExpirationDate = minDate;
    this.maxExpirationDate = maxDate;
  }

  private addDaysToDate(baseDate: Date, days: number): Date {
    const result = new Date(baseDate.getTime());
    result.setDate(result.getDate() + days);
    return result;
  }

  private buildDurationLabel(totalDays: number): string {
    if (totalDays <= 0) {
      return `0 ${this._translateService.instant('API_KEY_CONFIGURATION.DAY_PLURAL')}`;
    }

    const years = Math.floor(totalDays / 365);
    const remainingDays = totalDays % 365;
    const parts: string[] = [];

    if (years > 0) {
      parts.push(`${years} ${this.getDurationUnitLabel(years, 'year')}`);
    }

    if (remainingDays > 0) {
      parts.push(`${remainingDays} ${this.getDurationUnitLabel(remainingDays, 'day')}`);
    }

    if (parts.length === 2) {
      const joiner = this._translateService.instant('API_KEY_CONFIGURATION.DURATION_JOINER');
      return `${parts[0]} ${joiner} ${parts[1]}`;
    }

    return parts[0];
  }

  private buildHoursDurationLabel(totalHours: number): string {
    if (totalHours <= 0) {
      return `0 ${this._translateService.instant('API_KEY_CONFIGURATION.HOUR_PLURAL')}`;
    }

    if (totalHours < 24) {
      return `${totalHours} ${this.getHourUnitLabel(totalHours)}`;
    }

    const totalDays = totalHours / 24;
    if (Number.isInteger(totalDays)) {
      return this.buildDurationLabel(totalDays);
    }

    return `${totalHours} ${this.getHourUnitLabel(totalHours)}`;
  }

  private getHourUnitLabel(value: number): string {
    return this._translateService.instant(
      value === 1 ? 'API_KEY_CONFIGURATION.HOUR_SINGULAR' : 'API_KEY_CONFIGURATION.HOUR_PLURAL'
    );
  }

  private initializeBlobLifeTimeOptions(): void {
    this.blobLifeTimeDaysOptions = Array.from({ length: (this.blobsLifeTimeRangeHours.max / 24) + 1 }, (_, index) => index);
    this.blobLifeTimeHoursOptions = Array.from({ length: 24 }, (_, index) => index);
  }

  private setBlobsLifeTimeSelectionFromValue(value: string, isConfigured: boolean): void {
    const totalHours = this.parseBlobsLifeTimeToHours(value);

    if (!isConfigured) {
      this.selectedBlobLifeTimeDays = 0;
      this.selectedBlobLifeTimeHours = 1;
      this.updateBlobsLifeTimeFromSelection();
      return;
    }

    if (totalHours === null) {
      this.selectedBlobLifeTimeDays = 0;
      this.selectedBlobLifeTimeHours = 0;
      this.syncBlobsLifeTimeInputValues();
      this.apiKeyConfiguration.BlobsLifeTime = '';
      return;
    }

    const totalHoursInteger = Math.floor(totalHours);
    this.selectedBlobLifeTimeDays = Math.floor(totalHoursInteger / 24);
    this.selectedBlobLifeTimeHours = totalHoursInteger % 24;
    this.updateBlobsLifeTimeFromSelection();
  }

  private updateBlobsLifeTimeFromSelection(): void {
    const days = this.selectedBlobLifeTimeDays;
    const hours = this.selectedBlobLifeTimeHours;

    this.syncBlobsLifeTimeInputValues();

    if (days > 0) {
      this.apiKeyConfiguration.BlobsLifeTime = `${days}.${hours.toString().padStart(2, '0')}:00:00`;
      return;
    }

    this.apiKeyConfiguration.BlobsLifeTime = `${hours.toString().padStart(2, '0')}:00:00`;
  }

  private normalizeDate(value: Date | null): Date | null {
    if (!value || !(value instanceof Date) || isNaN(value.getTime())) {
      return null;
    }

    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  private parseDateValue(value: Date | string): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    const parsedDate = new Date(value);
    return isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  private parseBlobsLifeTimeToHours(value: string): number | null {
    if (!value) {
      return null;
    }

    const normalizedValue = value.trim();
    if (!normalizedValue) {
      return null;
    }

    if (/^\d+$/.test(normalizedValue)) {
      return Number(normalizedValue);
    }

    const dayTimeMatch = normalizedValue.match(/^(\d+)\.(\d{1,2}):([0-5]\d):([0-5]\d)$/);
    if (dayTimeMatch) {
      const days = Number(dayTimeMatch[1]);
      const hours = Number(dayTimeMatch[2]);
      const minutes = Number(dayTimeMatch[3]);
      const seconds = Number(dayTimeMatch[4]);

      return (days * 24) + hours + (minutes / 60) + (seconds / 3600);
    }

    const timeMatch = normalizedValue.match(/^(\d{1,4}):([0-5]\d):([0-5]\d)$/);
    if (!timeMatch) {
      return null;
    }

    const hours = Number(timeMatch[1]);
    const minutes = Number(timeMatch[2]);
    const seconds = Number(timeMatch[3]);

    return hours + (minutes / 60) + (seconds / 3600);
  }

  private parseAndClampLifetimeValue(rawValue: number | string, max: number): number {
    const onlyDigitsValue = `${rawValue || ''}`.replace(/\D/g, '');

    if (!onlyDigitsValue) {
      return 0;
    }

    return Math.min(max, Number(onlyDigitsValue));
  }

  private scrollBlobLifeTimeOptionIntoView(type: 'days' | 'hours', value: number): void {
    setTimeout(() => {
      const optionsContainerRef = type === 'days'
        ? this.blobLifeTimeDaysOptionsContainerRef
        : this.blobLifeTimeHoursOptionsContainerRef;

      const optionsContainer = optionsContainerRef && optionsContainerRef.nativeElement;
      if (!optionsContainer) {
        return;
      }

      const optionElement = optionsContainer.querySelector(`[data-option-value="${value}"]`) as HTMLElement;
      if (!optionElement) {
        return;
      }

      const centeredTop = optionElement.offsetTop - (optionsContainer.clientHeight / 2) + (optionElement.clientHeight / 2);
      optionsContainer.scrollTop = Math.max(0, centeredTop);
    });
  }

  private syncBlobsLifeTimeInputValues(): void {
    this.blobLifeTimeDaysInputValue = this.selectedBlobLifeTimeDays;
    this.blobLifeTimeHoursInputValue = this.selectedBlobLifeTimeHours;
  }

  private getDurationUnitLabel(value: number, unit: 'day' | 'year'): string {
    if (unit === 'year') {
      return this._translateService.instant(
        value === 1 ? 'API_KEY_CONFIGURATION.YEAR_SINGULAR' : 'API_KEY_CONFIGURATION.YEAR_PLURAL'
      );
    }

    return this._translateService.instant(
      value === 1 ? 'API_KEY_CONFIGURATION.DAY_SINGULAR' : 'API_KEY_CONFIGURATION.DAY_PLURAL'
    );
  }

  getCurrentEnvironment(): void {
    this._apiKeyService.getCurrentEnvironment().subscribe((currentEnvironment: string) => {
      this.currentEnvironment = currentEnvironment;
      if (currentEnvironment.toLowerCase() === 'development') {
        this.isDevelopEnvironment = true;
      };
    });
  }

  private shouldValidateConfiguredFields(): boolean {
    return !!(this.apiKeyConfiguration && this.apiKeyConfiguration.Enabled);
  }

}
